/** The header strip over a visit's lines (folder 44 §5.2; moved from `builder/rows.tsx` at step 8). */
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { IconLock } from '../../frame/icons.js'
import { Popover } from '../../components/overlays.js'
import { COLUMN_LABELS, COLUMN_TIPS } from '../copy.js'
import { InternalBand, lineGrid, useLineGrid } from './grid.js'

/** The worked examples a header's tip shows, all from one line the server sent. */
export interface LineHeaderExample {
  qty: string
  priceEach: string
  price: ReactNode
  cost: ReactNode
  margin: ReactNode
}

/**
 * The one header strip, directly above a visit's first service (§5.2).
 *
 * The same `lineGrid(mode)` the rows use, so the two cannot drift. Each label
 * is a button opening a 330px tip — on hover or focus with a mouse, on a tap
 * on an iPad — and the internal two sit on the tint with the lock.
 */
export function LineHeader({ example }: { example: LineHeaderExample | null }) {
  const { mode, qty, targetPct } = useLineGrid()
  return (
    <div
      data-line-header
      className={cn(lineGrid(mode, qty),
        'h-[30px] border-t border-line-soft bg-surface pl-[66px] pr-3.5')}
    >
      <span />
      <span />
      <span className="col-span-2">
        {qty === 'rule'
          /*
           * A rule's header is the word alone: the Qty tip ("Type to
           * override…") says what a typed number does, which a rule is not,
           * and Board 5 draws no tip of its own for it.
           */
          ? <span className="text-label uppercase text-text-faint">{COLUMN_LABELS.qtyRule}</span>
          : <HeaderTip label={COLUMN_LABELS.qty} tip={COLUMN_TIPS.qty} example={example?.qty} align="start" />}
      </span>
      {mode === 'all' && (
        <HeaderTip label={COLUMN_LABELS.priceEach} tip={COLUMN_TIPS.priceEach} example={example?.priceEach} />
      )}
      <HeaderTip label={COLUMN_LABELS.price} tip={COLUMN_TIPS.price} example={example?.price} strong />
      <InternalBand
        inset="my-0"
        cost={<HeaderTip label={COLUMN_LABELS.cost} tip={COLUMN_TIPS.cost} example={example?.cost} internal />}
        margin={(
          <HeaderTip
            label={COLUMN_LABELS.margin}
            tip={COLUMN_TIPS.margin.replace('{target}', String(targetPct))}
            example={example?.margin}
            internal
            lock={false}
          />
        )}
      />
      <span />
    </div>
  )
}

function HeaderTip({ label, tip, example, strong, internal, lock = internal, align = 'end' }: {
  label: string
  tip: string
  example?: ReactNode
  /** Price is the customer's number, so its label is ink rather than faint. */
  strong?: boolean
  internal?: boolean
  /** The lock beside the label — on Cost only; the band says the rest (qb-02). */
  lock?: boolean
  align?: 'start' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)

  return (
    <span className={cn('flex', align === 'end' ? 'justify-end' : 'justify-start')}>
      <button
        ref={setAnchor}
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((was) => !was)}
        className={cn('inline-flex items-center gap-1 whitespace-nowrap text-label uppercase',
          'underline decoration-dotted decoration-1 underline-offset-[3px]',
          strong ? 'text-text' : 'text-text-faint')}
      >
        {label}
        {lock && <IconLock size={10} />}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
        width={330}
        align={align === 'end' ? 'right' : 'left'}
        label={label}
        marker="column-tip"
        className={cn('px-3.5 py-3', internal && 'bg-surface-alt')}
      >
        <span className="flex items-center gap-1.5 text-label uppercase text-text">
          {label}
          {internal && <IconLock size={10} className="text-text-faint" />}
        </span>
        <p className="mt-1.5 text-[12.5px] leading-[1.45] text-text">{tip}</p>
        {example !== undefined && (
          <div className={cn('tnum mt-2 rounded-field border border-line-soft px-[9px] py-[7px]',
            'text-[12px] text-text-muted', internal ? 'bg-surface' : 'bg-surface-alt')}>
            {example}
          </div>
        )}
      </Popover>
    </span>
  )
}
